import { useVerifyQdsQueryParamsAndConfirmEmail } from "./hooks";
import { Loader } from "../../modules/loader";
import "./ClientQdsSignScreen.style.css";
import { FilePreview } from "../../components/file-preview";
import { QdsSplitPagesComponent } from "./QdsSplitPagesComponent";
import { QdsSignInstructionPanel } from "./QdsSignInstructionPanel";
import { QdsSignModal } from "./QdsSignModal";
import { extractQueryParamValue } from "./utils/QueryParams";
import DigitalSignatureQualifiedGateway from "../../api/gateways/DigitalSignatureQualifiedGateway";
import { useToast } from "../../components/toast";
import {
  useIsQdsSigningSelector,
  useFetchedQdsDocumentSelector,
  useQdsDocumentIsSuccessfullySignedSelector,
  useQdsDocumentBlobUrlSelector,
} from "../../store/qdsDocuments";
import { useTranslation } from "react-i18next";
import { QdsWebHeader } from "./QdsWebHeader";
import { useState } from "react";

export const ClientQdsSignScreen = () => {
  const { isLoading, isError } = useVerifyQdsQueryParamsAndConfirmEmail();
  const isSigning = useIsQdsSigningSelector();
  const fetchedDocumentData = useFetchedQdsDocumentSelector();
  const { t } = useTranslation();
  const documentIsSuccessfullySigned = useQdsDocumentIsSuccessfullySignedSelector();
  const documentBlobUrl = useQdsDocumentBlobUrlSelector();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { success, danger } = useToast();

  const isDocumentAlreadySigned = fetchedDocumentData?.qdsSignature?.status === "signed";

  if (isLoading)
    return (
      <>
        <QdsWebHeader />
        <div className="d-flex justify-content-center qds-loader-container">
          <Loader color="primary" size="l" />
        </div>
      </>
    );

  if (isError)
    return (
      <>
        <QdsWebHeader />
        <div className="d-flex justify-content-center align-items-center qds-error-container">
          <div className="text-center">
            <h2 className="mb-5">Verification Failed</h2>
            <p>
              Please contact our operator for more information at <strong>0001 7600 23</strong>
            </p>
          </div>
        </div>
      </>
    );

  if (isDocumentAlreadySigned || documentIsSuccessfullySigned) {
    return (
      <>
        <QdsWebHeader />
        <div className="d-flex justify-content-center align-items-center qds-already-signed-container">
          <div className="text-center">
            <h2 className="mb-4">{t("WEB_QDS_SIGNATURE.ALREADY_SIGNED_TITLE")}</h2>
            <p>{t("WEB_QDS_SIGNATURE.ALREADY_SIGNED_MESSAGE")}</p>
          </div>
        </div>
      </>
    );
  }

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleConfirmSignature = async (file: File) => {
    const token = extractQueryParamValue("token");
    const email = extractQueryParamValue("email");

    if (!token || !email) {
      danger("Missing authentication parameters");
      return;
    }

    setIsUploading(true);
    handleCloseModal();

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("token", token);
      formData.append("email", email);

      await DigitalSignatureQualifiedGateway.submitWebQdsSignedDocument(formData);
      
      // Notify other tabs that QDS signature was updated
      const qdsRequestId = fetchedDocumentData?.qdsRequest?.id;
      if (qdsRequestId) {
        const channel = new BroadcastChannel('qds_signature_update');
        channel.postMessage({ qdsRequestId });
        channel.close();
      }
      
      success("Dokument je uspešno potpisan");
      
      // Reload page after showing success message
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error("Error uploading signed document:", error);
      danger("Došlo je do greške. Molim pokušajte ponovo");
      setIsUploading(false);
    }
  };

  const lastFileIndex = (fetchedDocumentData?.document?.files?.length || 0) - 1;

  return (
    <>
      <QdsWebHeader />
      {fetchedDocumentData ? (
        <div className="qds-preview-container">
          <div className="qds-pages-container">
            {isSigning ? (
              <QdsSplitPagesComponent />
            ) : (
              <div
                style={{
                  position: "sticky",
                  top: 128,
                  maxWidth: 800,
                  margin: "0 auto",
                }}
              >
                <FilePreview
                  enableCanceling={false}
                  enableEditing={false}
                  type={fetchedDocumentData.document?.files?.[lastFileIndex]?.mimeType ?? ""}
                  url={documentBlobUrl ?? ""}
                  name={fetchedDocumentData.document?.files?.[lastFileIndex]?.name ?? ""}
                  width={800}
                  height={800}
                />
              </div>
            )}
          </div>

          <div className="qds-signature-panel">
            {!isSigning && !documentIsSuccessfullySigned && !isUploading ? (
              <button
                onClick={handleOpenModal}
                style={{
                  width: "100%",
                  height: 50,
                  fontSize: 16,
                  cursor: "pointer",
                  borderRadius: 4,
                  border: "none",
                  backgroundColor: "#00264d",
                  color: "#fff",
                }}
              >
                {t("WEB_QDS_SIGNATURE.DOWNLOAD_FOR_SIGNING")}
              </button>
            ) : isSigning ? (
              <QdsSignInstructionPanel />
            ) : null}
          </div>
        </div>
      ) : null}

      {isModalOpen && (
        <QdsSignModal
          documentId={fetchedDocumentData?.document?.id}
          qdsRequestId={fetchedDocumentData?.qdsRequest?.id}
          documentName={fetchedDocumentData?.document?.files?.[lastFileIndex]?.name}
          email={extractQueryParamValue("email") || ""}
          token={extractQueryParamValue("token") || ""}
          onClose={handleCloseModal}
          onConfirm={handleConfirmSignature}
          autoDownload
        />
      )}
    </>
  );
};
